import React, { useCallback, useContext, useState } from "react";
import { createClasses, TransparentButton } from "@bloomberg/media-ui-core";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import type { TickerChartDataPoint } from "@bloomberg/media-ui-markets";
import styles from "./styles.module.css";
import WatchlistDashboardTitle from "~components/WatchlistDashboardTitle";
import ComparisonChart from "~components/Watchlist/delta/ComparisonChart";
import ComparisonSettings from "~components/Watchlist/delta/ComparisonSettings";
import ComparisonChartLegend from "~components/Watchlist/delta/ComparisonChart/ComparisonChartLegend";
import {
  useIsComparisonMaximized,
  WatchlistContext,
} from "~context/WatchlistContext";

const DASHBOARD_V2_FEEDBACK_URL =
  "https://www.bloomberg.com/feedback?page=watchlist-comparison";

export default function ComparisonChartWrapper() {
  const store = useContext(WatchlistContext);
  const isMaximized = useIsComparisonMaximized();

  // Hover/crosshair state for chart interaction
  const [hoveredChartData, setHoveredChartData] = useState<
    TickerChartDataPoint | undefined
  >(undefined);

  const toggleView = useCallback(() => {
    if (!store) return;

    const nextDashboardView = isMaximized ? "DASH" : "COMP";
    const clickEvent = isMaximized ? "watchlistHide" : "watchlistShow";
    const eventLabel = isMaximized
      ? "minimize_view_comparison_chart"
      : "expand_view_comparison_chart";

    trackClickEvent(clickEvent, eventLabel, "click", "watchlist");

    const { updateUserPreferences } = store.getState();

    // Update dashboard view - this will atomically update both dashboardView
    // and isComparisonMaximized together to prevent intermediate state flash
    void updateUserPreferences({ dashboardView: nextDashboardView });
  }, [isMaximized, store]);

  const cx = createClasses([
    styles.flexiCompareContainer,
    {
      [styles.maximized]: isMaximized,
    },
  ]);

  return (
    <div className={cx}>
      <WatchlistDashboardTitle
        title="Comparison Chart"
        description="Compare your watchlist's performance against market benchmarks. Performance is calculated based on both price movement and lot allocations."
        feedbackUrl={DASHBOARD_V2_FEEDBACK_URL}
        feedbackTrackingEventLabel="survey_comparison_chart"
        tooltipOpenEventLabel="tooltip_comparison_chart_open"
        tooltipCloseEventLabel="tooltip_comparison_chart_close"
      >
        <TransparentButton
          size="small"
          className={styles.seeMoreLessActionButton}
          onClick={toggleView}
        >
          {isMaximized ? "See Less" : "See More"}
        </TransparentButton>
      </WatchlistDashboardTitle>

      <div className={styles.chartWrapper}>
        {/* Left side: Comparison Chart */}
        <ComparisonChart
          isMaximized={isMaximized}
          handleChartInteraction={setHoveredChartData}
        >
          {/* Ticker Legend for minimized state (shown as overlay on chart) */}
          <ComparisonChartLegend
            className={styles.tickerChartLegend}
            hoveredDataPoint={hoveredChartData}
          />
        </ComparisonChart>

        {/* Right side: Comparison Configuration Panel (only shown when maximized) */}
        <div className={styles.compareConfigContainer}>
          <div className={styles.tickerChartLegendContainer}>
            {/* Interactive Ticker Legend for maximized state */}
            <ComparisonChartLegend
              className={styles.interactiveTickerChartLegend}
              hoveredDataPoint={hoveredChartData}
              enableTickerRemoval
              applyInteractiveStyles
            />

            {/* Search component for adding comparisons */}
            <ComparisonSettings />
          </div>
        </div>
      </div>
    </div>
  );
}
